import { useEffect, useRef, useState } from "react";
import type { AuthUser } from "@/types";

interface Props {
  authUser: AuthUser;
  onLogout: () => void;
  onGoToSettings: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
}

export function ProfileCard({ authUser, onLogout, onGoToSettings, theme, onToggleTheme }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const displayName = authUser.display_name || authUser.username;

  return (
    <div className="relative" ref={ref}>
      {/* Avatar trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-btn p-1 transition-colors hover:bg-surface-secondary"
      >
        {authUser.avatar ? (
          <img
            src={authUser.avatar}
            alt={displayName}
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-near-black text-xs font-bold text-white">
            {displayName[0].toUpperCase()}
          </div>
        )}
      </button>

      {/* Dropdown card */}
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-72 rounded-card bg-white shadow-card">
          {/* User info header */}
          <div className="flex items-center gap-3 border-b border-surface-secondary px-4 py-4">
            {authUser.avatar ? (
              <img
                src={authUser.avatar}
                alt={displayName}
                className="h-12 w-12 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-near-black text-sm font-bold text-white">
                {displayName[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-near-black">{displayName}</div>
              <div className="truncate text-xs text-text-secondary">@{authUser.username}</div>
              {authUser.email && (
                <div className="truncate text-xs text-text-secondary">{authUser.email}</div>
              )}
              <span className="mt-1 inline-flex rounded-badge bg-surface-secondary px-2 py-0.5 text-[10px] font-semibold text-text-secondary">
                {authUser.role_label}
              </span>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1">
            <button
              onClick={() => { setOpen(false); onGoToSettings(); }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-text-focused transition-colors hover:bg-surface-secondary"
            >
              <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
              </svg>
              Account Settings
            </button>

            <button
              onClick={onToggleTheme}
              className="flex w-full items-center justify-between px-4 py-2.5 text-sm text-text-focused transition-colors hover:bg-surface-secondary"
            >
              <div className="flex items-center gap-3">
                {theme === "light" ? (
                  <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" />
                  </svg>
                )}
                Theme
              </div>
              <span className="rounded-badge bg-surface-secondary px-2 py-0.5 text-[10px] font-semibold text-text-secondary capitalize">
                {theme}
              </span>
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-surface-secondary py-1">
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-error-text transition-colors hover:bg-rose-50"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
